import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type Dispatch, type FC, type SetStateAction, useEffect } from "react";
import { HeaderButton } from "@/components/headerButton/HeaderButton";
import { NursesMatrixGrid } from "@/components/NursesMatrixGrid/NursesMatrixGrid";
import { PanelDayFallback } from "@/components/StaffingChanges/PanelDayFallback";
import { type ScheduleWeeks, useScheduleWeeks } from "@/hooks/useScheduleWeeks";
import { useStaffShiftCounts } from "@/hooks/useStaffShiftCounts";
import { NEUTRAL, STATUS_BADGE } from "@/theme/tokens";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown/SystemDropdown";
import { trackEvent } from "@/utils/analytics";
import { type NurseData, ShiftType } from "@/utils/models";
import { formatDayName } from "@/utils/nursePanelUtils";
import { useMoveNursePanelData } from "./hooks";
import type { Direction, MoveNursePanelProps, MoveNursePanelSlotProps } from "./types";

const MoveNursePanelTitle: FC<{
	direction: Direction;
	setDirection: Dispatch<SetStateAction<Direction>>;
	workingDate: DateTime;
	targetShiftType: ShiftType;
}> = ({ direction, setDirection, workingDate, targetShiftType }) => {
	return (
		<>
			<Typography variant="h6" sx={{ fontWeight: 600, color: "primary.dark", justifySelf: "flex-start" }}>
				Move Nurse
			</Typography>
			<SystemDropdown
				label="Direction"
				items={[
					{ value: "from", label: "From" },
					{ value: "to", label: "To" },
				]}
				selectedItem={direction}
				onChange={(v) => setDirection(v === "to" ? "to" : "from")}
				formControlSx={{ width: "120px" }}
			/>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Typography sx={{ color: "primary.dark", fontWeight: 500 }}>
						{formatDayName(workingDate)}, {workingDate.toFormat("MMM D")}
					</Typography>
					<Box sx={{ ml: 0.5, display: "flex", alignItems: "center" }}>
						{targetShiftType === ShiftType.Night ? (
							<Box
								component="img"
								src="/images/moon.svg"
								alt="Night shift"
								sx={{
									width: 16,
									height: 16,
								}}
							/>
						) : (
							<Box
								component="img"
								src="/images/sun.svg"
								alt="Day shift"
								sx={{
									width: 16,
									height: 16,
								}}
							/>
						)}
					</Box>
				</Box>

				<Box sx={{ display: "flex", alignItems: "center", color: "primary.dark" }}>
					<IconButton size="small">{direction === "from" ? <ArrowForwardIosIcon fontSize="small" /> : <ArrowBackIos fontSize="small" />}</IconButton>
				</Box>

				<Box sx={{ display: "flex", alignItems: "center" }}>
					<Typography sx={{ color: "primary.dark", fontWeight: 500 }}>
						{formatDayName(workingDate)}, {workingDate.toFormat("MMM D")}
					</Typography>
					<Box sx={{ ml: 0.5, display: "flex", alignItems: "center" }}>
						{targetShiftType === ShiftType.Night ? (
							<Box
								component="img"
								src="/images/moon.svg"
								alt="Night shift"
								sx={{
									width: 16,
									height: 16,
								}}
							/>
						) : (
							<Box
								component="img"
								src="/images/sun.svg"
								alt="Day shift"
								sx={{
									width: 16,
									height: 16,
								}}
							/>
						)}
					</Box>
				</Box>
			</Box>
		</>
	);
};

const MoveNursePanelContent: FC<{
	tableNursesData: NurseData[];
	dateRange: DateTime[];
	setSelectedRowKey: Dispatch<SetStateAction<string | undefined>>;
	workingDayIndex: number;
}> = ({ tableNursesData, dateRange, setSelectedRowKey, workingDayIndex }) => {
	return (
		<>
			<Box
				sx={{
					display: "flex",
					justifyContent: "flex-end",
					p: 1,
					backgroundColor: NEUTRAL[50],
					borderBottom: `1px solid ${NEUTRAL[200]}`,
				}}
			>
				<Typography variant="caption" sx={{ fontSize: "12px", mr: 1 }}>
					<span style={{ color: STATUS_BADGE.WARN_INK, fontWeight: 500 }}>Day shift</span>
					<Box component="span" sx={{ px: 0.5 }}>
						•
					</Box>
					<span style={{ color: "var(--inhouse-primary)", fontWeight: 500 }}>Night shift</span>
				</Typography>
			</Box>
			<NursesMatrixGrid
				nursesData={tableNursesData}
				dateRange={dateRange}
				highlightDateIndexes={[workingDayIndex]}
				onSelectNurse={(nurse) => setSelectedRowKey(nurse?.rowKey)}
				isAdd
				hightlightOriginalShift
			/>
		</>
	);
};

const MoveNursePanelActions: FC<{
	onClose: () => void;
	handleMove: () => Promise<void>;
	selectedRowKey: string | undefined;
	isLoading: boolean;
}> = ({ onClose, handleMove, selectedRowKey, isLoading }) => {
	return (
		<Box sx={{ display: "flex", gap: 2 }}>
			<HeaderButton onPress={onClose} state="off" text="Cancel" disabled={false} secondary={true} />
			<HeaderButton onPress={handleMove} state="on" text="Move" disabled={typeof selectedRowKey !== "string" || isLoading} />
		</Box>
	);
};

const MoveNurseForSlot: FC<Omit<MoveNursePanelSlotProps, "open">> = ({ onClose, schedule, slot }) => {
	const { direction, setDirection, workingDate, targetShiftType, tableNursesData, dateRange, handleMove, selectedRowKey, isLoading, setSelectedRowKey } =
		useMoveNursePanelData({
			schedule,
			weekIndex: slot.weekIndex,
			shiftIndex: slot.shiftIndex,
			workingDayIndex: slot.dayIndex,
			onClose,
		});

	return (
		<Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
				<MoveNursePanelTitle direction={direction} setDirection={setDirection} workingDate={workingDate} targetShiftType={targetShiftType} />
			</Box>
			<Box sx={{ p: 0, maxHeight: "500px", overflowY: "auto" }}>
				<MoveNursePanelContent workingDayIndex={slot.dayIndex} tableNursesData={tableNursesData} dateRange={dateRange} setSelectedRowKey={setSelectedRowKey} />
			</Box>
			<Box sx={{ p: 2 }}>
				<MoveNursePanelActions onClose={onClose} handleMove={handleMove} selectedRowKey={selectedRowKey} isLoading={isLoading.moveNurse} />
			</Box>
		</Box>
	);
};

/** The panel's weeks around `date`, and its loading state: the weeks and the candidates' weekend counts. */
const useMovePanelWeeks = (date: DateTime): { schedule: ScheduleWeeks; panelDay: Pick<ScheduleWeeks, "isLoading" | "isError" | "refetch"> } => {
	const schedule = useScheduleWeeks(date);
	const counts = useStaffShiftCounts();
	const panelDay = {
		isLoading: schedule.isLoading || counts.isLoading,
		isError: schedule.isError || counts.isError,
		refetch: () => {
			schedule.refetch();
			counts.refetch();
		},
	};
	return { schedule, panelDay };
};

export const MoveNurse: FC<Omit<MoveNursePanelProps, "open">> = ({ date, shiftType, onClose }) => {
	const { schedule, panelDay } = useMovePanelWeeks(date);
	if (panelDay.isLoading || panelDay.isError) return <PanelDayFallback panelDay={panelDay} dataTestId="move-nurse-panel" />;
	const slot = schedule.slotOf(date, shiftType);
	// The panel only opens on a shift type that has staffing on its day.
	if (!slot) return null;
	return <MoveNurseForSlot onClose={onClose} schedule={schedule} slot={slot} />;
};

const MoveNursePanelForSlot: FC<MoveNursePanelSlotProps> = ({ open, onClose, schedule, slot }) => {
	const {
		staffingData,
		nursesData,
		direction,
		setDirection,
		workingDate,
		targetShiftType,
		tableNursesData,
		dateRange,
		handleMove,
		selectedRowKey,
		isLoading,
		setSelectedRowKey,
	} = useMoveNursePanelData({
		schedule,
		weekIndex: slot.weekIndex,
		shiftIndex: slot.shiftIndex,
		workingDayIndex: slot.dayIndex,
		onClose,
	});

	useEffect(() => {
		if (open) {
			trackEvent("Open Nurse recommendation Dialog", {
				staffingDate: staffingData,
				nursesData,
			});
		}
	}, [staffingData, nursesData, open]);

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			slotProps={{
				paper: {
					sx: {
						borderRadius: "8px",
						boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
						maxWidth: "96vw !important",
						width: "800px",
					},
				},
			}}
		>
			<DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
				<MoveNursePanelTitle direction={direction} setDirection={setDirection} workingDate={workingDate} targetShiftType={targetShiftType} />
			</DialogTitle>
			<DialogContent sx={{ p: 0 }}>
				<MoveNursePanelContent workingDayIndex={slot.dayIndex} tableNursesData={tableNursesData} dateRange={dateRange} setSelectedRowKey={setSelectedRowKey} />
			</DialogContent>
			<DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
				<MoveNursePanelActions onClose={onClose} handleMove={handleMove} selectedRowKey={selectedRowKey} isLoading={isLoading.moveNurse} />
			</DialogActions>
		</Dialog>
	);
};

export const MoveNursePanel: FC<MoveNursePanelProps> = ({ open, onClose, date, shiftType }) => {
	const { schedule, panelDay } = useMovePanelWeeks(date);
	if (panelDay.isLoading || panelDay.isError) return <PanelDayFallback panelDay={panelDay} dataTestId="move-nurse-panel" />;
	const slot = schedule.slotOf(date, shiftType);
	// The panel only opens on a shift type that has staffing on its day.
	if (!slot) return null;
	return <MoveNursePanelForSlot open={open} onClose={onClose} schedule={schedule} slot={slot} />;
};
